<template>
    <div>
      <v-card class="mx-auto my-2">
        <v-row align="center" no-gutters>
          <v-col cols="auto" class="d-flex align-center justify-center" style="max-width: 48px;">
            <v-checkbox v-model="isSelected" @change="toggleSelection" dense hide-details/>
          </v-col>
          <v-col>
            <v-card-text class="pb-0 d-flex justify-space-between align-center">
              <div>
                <small class="d-block subtitle-1 text--primary">
                  <span v-if="get(review, 'review_color')">
                    <v-badge inline dot :color="get(review, 'review_color')"></v-badge>
                  </span>
                  {{ get(review, 'account.account_name') }}
                </small>
                <small class="d-block subtitle-2 text--primary">{{ get(review, 'location.address.full_address') }}</small>
                <small class="d-block">Id: {{ get(review, 'location.store_code') }}</small>
              </div>
              <div>
                <small class="d-block text-right">{{ review.create_time_formatted }}</small>
                <small class="d-block text-right">{{ get(review, 'location.labels', []).join(' | ') }}</small>
              </div>
            </v-card-text>
          </v-col>
        </v-row>
        <v-card-text>
          <div class="d-flex">
            <v-avatar class="mr-4" color="primary" size="40">
              <img v-if="review.reviewer_photo_url" :src="review.reviewer_photo_url" :alt="review.reviewer_name">
              <span v-else class="white--text body-1 font-weight-medium">{{ getInitials(review.reviewer_name) }}</span>
            </v-avatar>
  
            <div class="full-width">
              <v-row align="center" class="mx-0 mb-0">
                <v-rating :value="review.star_rating" color="amber" dense half-increments readonly size="14"></v-rating>
              </v-row>
  
              <div class="subtitle-2 text--primary">
                {{ review.reviewer_name }}
              </div>
  
              <div v-if="review.comment">{{ review.comment }}</div>
              <div v-else>
                <p class="no-reply">O usuário não fez uma avaliação, apenas deu uma nota.</p>
              </div>
  
              <v-expand-transition>
                <div v-show="!expand && review.reply">
                  <v-divider class="mx-auto my-2" />
  
                  <div class="text--primary">{{ review.reply }}</div>
                </div>
              </v-expand-transition>
  
              <v-expand-transition>
                <div v-show="expand">
                  <div class="d-flex">
                    <v-textarea
                        rows="1"
                        auto-grow
                        placeholder="Resposta"
                        class="mt-4 mr-2"
                        dense
                        autofocus
                        outlined
                        v-model="reply"
                        v-on:keyup="onReplyKeyUp"
                    ></v-textarea>
                    <v-btn
                        :loading="loadingReply"
                        class="mt-4"
                        color="info"
                        @click="() => replyReview(reply)"
                    >
                      Responder
                    </v-btn>
                  </div>
  
                  <div>
                    Sua resposta será exibida <strong>publicamente</strong> no Google e deve obedecer às políticas de
                    conteúdo locais do Google.
                    <a href="https://policies.google.com/privacy" target="_blank">Termos de serviço</a>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </v-card-text>
  
        <v-card-text v-if="review.feeling || Array.isArray(review.keywords) && review.keywords.length > 0">
          <v-chip
              small
              v-if="review.feeling"
              :color="`${getFeelingColor(review.feeling)} accent-4 white--text`"
              class="mr-2"
          >
            {{ getFeelingName(review.feeling) }}
          </v-chip>
  
          <v-chip
              small
              v-for="keyword in review.keywords"
              :key="keyword.id"
              class="mr-2"
          >
            {{ keyword.name }}
          </v-chip>
        </v-card-text>
  
        <v-card-actions>
          <span v-if="otherUserIsResponding" class="text--secondary">Essa avaliação está sendo respondida por outro usuário</span>
          <span v-if="otherUserResponded" class="text--secondary">Essa avaliação foi respondida por outro usuário</span>
  
          <v-btn
              v-if="canReplyReview && review.status !== 'WAITING_APPROVAL' && !otherUserIsResponding && !otherUserResponded"
              color="info"
              text
              small
              @click="expand = !expand"
          >
            {{ expand ? 'Cancelar' : 'Responder' }}
          </v-btn>
  
          <confirm-modal
              v-if="canReplyReview &&
                  reply &&
                  review.status !== 'WAITING_APPROVAL' &&
                  !otherUserIsResponding &&
                  !otherUserResponded"
              small
              text
              :action="() => deleteReply()"
              action-text="Excluir resposta"
              action-color="info"
              title="Deseja excluir a resposta?"
          />
  
          <review-detail-modal
              v-if="canReplyReview &&
                  review.status !== 'WAITING_APPROVAL' &&
                  !otherUserIsResponding &&
                  !otherUserResponded"
              :on-success="reviewUpdated"
              :review="review"
          />
  
          <confirm-modal
              v-if="canReplyReview &&
                  review.status !== 'WAITING_APPROVAL' &&
                  !otherUserIsResponding &&
                  !otherUserResponded"
              small
              text
              :action="ignoreReview"
              :action-text="review.ignored === true ? 'Desfazer ignorar' : 'Ignorar'"
              action-color="info"
              :title="review.ignored === true ? 'Tornar avaliação visível' : 'Deseja ignorar essa avaliação?'"
              :description="review.ignored === true ? 'A avaliação voltará a aparecer na lista principal.' : 'Avaliações ignoradas não aparecem na lista principal.'"
          />
  
          <confirm-modal
              width="600px"
              v-if="canApproveReview &&
                  review.status === 'WAITING_APPROVAL' &&
                  !otherUserIsResponding &&
                  !otherUserResponded"
              small
              text
              :action="approveReview"
              action-text="Aprovar"
              action-color="info"
              title="Deseja aprovar a resposta dessa avaliação?"
          />
  
          <confirm-modal
              width="600px"
              v-if="canApproveReview &&
                  review.status === 'WAITING_APPROVAL' &&
                  !otherUserIsResponding &&
                  !otherUserResponded"
              small
              text
              :action="disapproveReview"
              action-text="Reprovar"
              action-color="info"
              title="Deseja reprovar a resposta dessa avaliação?"
          />
        </v-card-actions>
      </v-card>
  
      <v-snackbar
          v-model="snackbar.show"
          timeout="3000"
          :color="snackbar.color"
      >
        {{ snackbar.text }}
  
        <template v-slot:action="{ attrs }">
          <v-btn
              color="white"
              text
              v-bind="attrs"
              @click="snackbar.show = false"
          >
            Ok
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </template>
  
  <script>
  import ConfirmModal from '@/components/ConfirmModal'
  import ReviewDetailModal from '@/views/Reviews/ReviewDetailModal'
  import { get } from 'lodash'
  import {handleResponseError} from '@/api/handleError'
  import hasPermission from '@/helpers/hasPermission'
  import { database } from '@/plugins/firebase'
  import { ref, set, onValue } from "firebase/database"
  import { mapState } from 'vuex'
  import {STATUS_IDLE, STATUS_RESPONDED, STATUS_RESPONDING, TEN_MINUTES_IN_MILLISECONDS} from '@/constants/reviews'
  import {CAN_APPROVE_REVIEW, CAN_REPLY_REVIEW} from '@/constants/permissions'
   
  export default {
    name: 'CardReviewPending',
    props: {
      review: {
        type: Object,
        required: true,
      },
      firebaseRef: String,
      onIgnored: Function,
      onReplied: Function,
      onReplyDeleted: Function,
      onReviewUpdated: Function,
      onApproved: Function,
      onDisapproved: Function,
    },
    computed: {
      ...mapState({
        user: state => state.auth.user,
        selectedReviews: state => state.reviews.selectedReviews,
      }),
      isSelected: {
        get() {
          return this.selectedReviews.includes(this.review.id);
        },
        set(value) {
          this.toggleSelection(value);
        }
      },
      get() {
        return get
      },
      hasPermission() {
        return hasPermission
      },
      canApproveReview() {
        return hasPermission([CAN_APPROVE_REVIEW])
      },
      canReplyReview() {
        return hasPermission([CAN_REPLY_REVIEW])
      }
    },
    components: {
      ConfirmModal,
      ReviewDetailModal,
    },
    methods: {
      showNotification(message, color) {
        this.snackbar.text = message
        this.snackbar.show = true
        this.snackbar.color = color
      },
      getInitials (name) {
        return (name || '').toString().split(' ').map(l => l.charAt(0)).slice(0, 2).join('')
      },
      getFeelingColor (feeling) {
        switch (feeling) {
          case 'POSITIVE':
            return 'green'
          case 'NEGATIVE':
            return 'red'
          case 'NEUTRAL':
            return 'info'
        }
      },
      getFeelingName (feeling) {
        switch (feeling) {
          case 'POSITIVE':
            return 'Positivo'
          case 'NEGATIVE':
            return 'Negativo'
          case 'NEUTRAL':
            return 'Neutro'
        }
      },
      toggleSelection(value) {
        const selected = [...this.selectedReviews];
        if (value) {
          if (!selected.includes(this.review.id)) {
            selected.push(this.review.id);
          }
        } else {
          const index = selected.indexOf(this.review.id);
          if (index !== -1) {
            selected.splice(index, 1);
          }
        }
        this.$store.commit('reviews/setSelectedReviews', selected);
      },
      ignoreReview: async function () {
        this.loadingIgnore = true
        await this.$store.dispatch('reviews/ignore', this.review.id)
            .then(() => {
              if (this.onIgnored) this.onIgnored()
            }).catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao ignorar a avaliação.')
              this.showNotification(message, 'red darken-2')
              this.loadingIgnore = false
            })
        this.loadingIgnore = false
      },
      approveReview: async function () {
        this.loadingApprove = true
        await this.$store.dispatch('reviews/approve', this.review.id)
            .then(() => {
              if (this.onApproved) this.onApproved()
            }).catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao aprovar a avaliação.')
              this.showNotification(message, 'red darken-2')
              this.loadingApprove = false
            })
        this.loadingApprove = false
      },
      disapproveReview: async function () {
        this.loadingDisapprove = true
        await this.$store.dispatch('reviews/disapprove', this.review.id)
            .then(() => {
              if (this.onDisapproved) this.onDisapproved()
            }).catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao reprovar a avaliação.')
              this.showNotification(message, 'red darken-2')
              this.loadingDisapprove = false
            })
        this.loadingDisapprove = false
      },
      reviewUpdated: async function () {
        if (this.onReviewUpdated) this.onReviewUpdated()
      },
      replyReview: async function (reply) {
        this.reply = reply
        this.loadingReply = true
        await this.$store.dispatch('reviews/reply', { id: this.review.id, data: { reply } })
            .then(() => {
              if (this.onReplied) this.onReplied()
              this.expand = false
  
              if (reply && reply !== '') {
                this.fbSendStatus(STATUS_RESPONDED)
              } else {
                this.fbSendStatus(STATUS_IDLE)
                clearTimeout(this.idleTimeout)
              }
            })
            .catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao atualizar a avaliação.')
              this.showNotification(message, 'red darken-2')
              this.loadingReply = false
            })
        this.loadingReply = false
      },
      deleteReply: async function () {
        this.loadingReply = true
        await this.$store.dispatch('reviews/deleteReply', { id: this.review.id })
            .then(() => {
              if (this.onReplyDeleted) this.onReplyDeleted()
              this.expand = false
  
              this.fbSendStatus(STATUS_IDLE)
              clearTimeout(this.idleTimeout)
            })
            .catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao excluir resposta.')
              this.showNotification(message, 'red darken-2')
              this.loadingReply = false
            })
        this.loadingReply = false
      },
      fbSendStatus: async function(status) {
        const FB_DB_REF = process.env.VUE_APP_FIREBASE_DATABASE
        if (this.firebaseRef) {
          await set(ref(database, FB_DB_REF + this.firebaseRef), {
            status,
            reviewId: this.review.review_id,
            userId: this.user.id,
            date: new Date().getTime()
          })
        }
      },
      onReplyKeyUp() {
        clearTimeout(this.replyTimeout)
        clearTimeout(this.idleTimeout)
        this.replyTimeout = setTimeout(() => this.fbSendStatus(STATUS_RESPONDING), 300)
        this.idleTimeout = setTimeout(() => this.fbSendStatus(STATUS_IDLE), TEN_MINUTES_IN_MILLISECONDS)
      }
    },
    watch: {
      expand(value) {
        this.fbSendStatus(value ? STATUS_RESPONDING : STATUS_IDLE)
        if (value) {
          clearTimeout(this.idleTimeout)
          this.idleTimeout = setTimeout(() => {
            this.fbSendStatus(STATUS_IDLE)
            this.expand = false
          }, TEN_MINUTES_IN_MILLISECONDS)
        }
      }
    },
    created() {
      this.reply = this.review.reply
      const FB_DB_REF = process.env.VUE_APP_FIREBASE_DATABASE
      if (this.firebaseRef && !hasPermission([CAN_APPROVE_REVIEW])) {
        const myRef = ref(database, FB_DB_REF + this.firebaseRef)
        onValue(myRef, (snapshot) => {
          const data = snapshot.val()
          const now = new Date().getTime()
  
          // Ninguem esta respondendo ou é o usuário logado
          if (!data || (data && data.status === STATUS_IDLE) || data.userId === this.user.id) {
            this.otherUserIsResponding = false
            this.otherUserResponded = false
          }
          // Se tem mais 10 minutos que essa review esta sendo respondida ela volta pra IDLE
          else if (data.userId !== this.user.id && now - data.date >= TEN_MINUTES_IN_MILLISECONDS && data.status !== STATUS_RESPONDED) {
            this.otherUserIsResponding = false
            this.otherUserResponded = false
            this.fbSendStatus(STATUS_IDLE)
          }
          // Outro usuario esta respondendo
          else if (data.userId !== this.user.id && data.status === STATUS_RESPONDING) {
            this.otherUserIsResponding = true
            this.otherUserResponded = false
            this.expand = false
          }
          // Outro usuario respondeu
          else if (data.userId !== this.user.id && data.status === STATUS_RESPONDED) {
            this.otherUserIsResponding = false
            this.otherUserResponded = true
            this.expand = false
          }
          else {
            this.otherUserIsResponding = false
            this.otherUserResponded = false
          }
        })
      }
    },
    data: () => ({
      otherUserIsResponding: false,
      otherUserResponded: false,
      replyTimeout: null,
      idleTimeout: null,
      expand: false,
      reply: '',
      loadingIgnore: false,
      loadingUpdate: false,
      loadingReply: false,
      loadingDisapprove: false,
      loadingApprove: false,
      snackbar: {
        text: '',
        show: false,
        color: 'info',
      }
    }),
  }
  </script>
  
  <style scoped>
  a {
    text-decoration: none;
  }
  .full-width {
    width: 100%;
  }
  .no-reply{
    font-style: italic;
    margin: 0px;
  }
  .v-checkbox {
    margin-top: 0px !important;
    padding: 0px;
  }
  </style>